import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { BookOpen, Star, ThumbsUp, Meh, ThumbsDown, MoreVertical, RefreshCw, Trash2, Check, Loader2, Trophy, Plus, Calendar, Tag, ChevronRight, Lock, FileText, Library, ArrowRight, Search, Skull, Ghost, Heart, Wand2, Rocket, Clock, Map, Baby, Sparkles, Users, Lightbulb, Briefcase, FlaskConical, Feather, Mountain, Drama, BookText, GraduationCap, Utensils, Plane, Brain, Palette, Building2, Swords } from 'lucide-react';
import { toast } from 'sonner';
import { AppHeader } from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Genre configuration for books with icons matching TV/Film style
const genreConfig = {
  'Fiction': { icon: BookText, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  'Non-Fiction': { icon: GraduationCap, color: 'bg-slate-50 text-slate-600 border-slate-200' },
  'Mystery': { icon: Search, color: 'bg-purple-50 text-purple-600 border-purple-200' },
  'Science Fiction': { icon: Rocket, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Fantasy': { icon: Wand2, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'Romance': { icon: Heart, color: 'bg-pink-50 text-pink-600 border-pink-200' },
  'Thriller': { icon: Skull, color: 'bg-red-50 text-red-600 border-red-200' },
  'Horror': { icon: Ghost, color: 'bg-neutral-50 text-neutral-700 border-neutral-300' },
  'Biography': { icon: Users, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'Memoir': { icon: Feather, color: 'bg-amber-50 text-amber-600 border-amber-200' },
  'History': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Historical Fiction': { icon: Clock, color: 'bg-orange-50 text-orange-600 border-orange-200' },
  'Science': { icon: FlaskConical, color: 'bg-teal-50 text-teal-600 border-teal-200' },
  'Self-Help': { icon: Lightbulb, color: 'bg-green-50 text-green-600 border-green-200' },
  'Business': { icon: Briefcase, color: 'bg-blue-50 text-blue-600 border-blue-200' },
  'Children\'s': { icon: Baby, color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  'Young Adult': { icon: Sparkles, color: 'bg-fuchsia-50 text-fuchsia-600 border-fuchsia-200' },
  'Classics': { icon: BookOpen, color: 'bg-stone-50 text-stone-600 border-stone-200' },
  'Adventure': { icon: Map, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  'Crime': { icon: Search, color: 'bg-slate-50 text-slate-700 border-slate-300' },
  'Dystopian': { icon: Building2, color: 'bg-gray-50 text-gray-700 border-gray-300' },
  'Contemporary': { icon: Drama, color: 'bg-rose-50 text-rose-600 border-rose-200' },
  'Literary Fiction': { icon: Feather, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'Philosophy': { icon: Brain, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  'Psychology': { icon: Brain, color: 'bg-violet-50 text-violet-600 border-violet-200' },
  'Poetry': { icon: Feather, color: 'bg-pink-50 text-pink-500 border-pink-200' },
  'Graphic Novel': { icon: Palette, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  'Comics': { icon: Palette, color: 'bg-sky-50 text-sky-600 border-sky-200' },
  'Cookbooks': { icon: Utensils, color: 'bg-orange-50 text-orange-500 border-orange-200' },
  'Travel': { icon: Plane, color: 'bg-cyan-50 text-cyan-600 border-cyan-200' },
  'Action': { icon: Swords, color: 'bg-red-50 text-red-600 border-red-200' },
  'Western': { icon: Mountain, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  'Middle Grade': { icon: Baby, color: 'bg-lime-50 text-lime-600 border-lime-200' },
};

const getGenreConfig = (genre) => {
  return genreConfig[genre] || { icon: BookOpen, color: 'bg-slate-50 text-slate-600 border-slate-200' };
};

const StatCard = ({ label, value, icon, color }) => {
  const colorClasses = {
    coral: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-yellow-100 text-yellow-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  
  return (
    <Card className="border-0 shadow-lg bg-white/95 backdrop-blur-sm">
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-[#3c405b]">{value}</p>
          <p className="text-sm text-[#64748B]">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default function BooksReadPage({ user }) {
  const navigate = useNavigate();
  const [readBooks, setReadBooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Genre filter state
  const [selectedGenre, setSelectedGenre] = useState(null);
  
  // Re-rank dialog state - now includes full rating flow
  const [rerankingBook, setRerankingBook] = useState(null);
  const [rerankStep, setRerankStep] = useState('rating'); // 'rating' | 'comparison'
  const [rerankGrade, setRerankGrade] = useState(null);
  const [rerankComment, setRerankComment] = useState('');
  const [rerankDate, setRerankDate] = useState(null);
  const [comparisonStep, setComparisonStep] = useState(false);
  const [existingItems, setExistingItems] = useState([]);
  const [comparisonItem, setComparisonItem] = useState(null);
  const [searchLow, setSearchLow] = useState(0);
  const [searchHigh, setSearchHigh] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // View details state
  const [viewingDetails, setViewingDetails] = useState(null);
  
  // Custom genre dialog state
  const [customGenreDialog, setCustomGenreDialog] = useState({ open: false, bookId: null });
  const [customGenreInput, setCustomGenreInput] = useState('');
  
  const showScores = (stats?.total_books || 0) >= 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [readRes, statsRes] = await Promise.all([
        axios.get(`${API}/read-books`, { withCredentials: true }),
        axios.get(`${API}/read-books/stats`, { withCredentials: true })
      ]);
      setReadBooks(readRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from your read list?`)) return;
    
    try {
      await axios.delete(`${API}/read-books/${item.id}`, { withCredentials: true });
      toast.success('Removed from read list');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove');
    }
  };

  const handleStartRerank = async (item) => {
    setRerankingBook(item);
    // Start with rating step - pre-fill with current values
    setRerankStep('rating');
    setRerankGrade(item.grade || null);
    setRerankComment(item.comment || '');
    setRerankDate(item.read_date ? new Date(item.read_date) : null);
  };

  const handleContinueToComparison = async () => {
    // Check if grade changed - if so, need to compare against new grade group
    const targetGrade = rerankGrade;
    const sameGrade = readBooks.filter(b => b.grade === targetGrade && b.id !== rerankingBook.id)
      .sort((a, b) => a.rank_in_group - b.rank_in_group);
    
    setExistingItems(sameGrade);
    
    if (sameGrade.length === 0) {
      // No other books in this grade - save directly
      await saveRerankWithDetails(1);
    } else {
      // Start 1v1 comparison
      setSearchLow(0);
      setSearchHigh(sameGrade.length);
      const midIndex = Math.floor(sameGrade.length / 2);
      setComparisonItem(sameGrade[midIndex]);
      setRerankStep('comparison');
      setComparisonStep(true);
    }
  };

  const handleComparisonChoice = async (preferReranking) => {
    let newLow = searchLow;
    let newHigh = searchHigh;
    
    const currentIndex = existingItems.findIndex(i => i.id === comparisonItem.id);
    
    if (preferReranking) {
      newHigh = currentIndex;
    } else {
      newLow = currentIndex + 1;
    }
    
    setSearchLow(newLow);
    setSearchHigh(newHigh);
    
    if (newLow >= newHigh) {
      await saveRerankWithDetails(newLow + 1);
    } else {
      const midIndex = Math.floor((newLow + newHigh) / 2);
      setComparisonItem(existingItems[midIndex]);
    }
  };

  const saveRerankWithDetails = async (newRank) => {
    setSaving(true);
    try {
      // First update the book's grade, comment, and date if changed
      const updates = {
        grade: rerankGrade,
        comment: rerankComment,
        read_date: rerankDate ? rerankDate.toISOString().split('T')[0] : null
      };
      
      await axios.put(`${API}/read-books/${rerankingBook.id}`, updates, { withCredentials: true });
      
      // Now update the ranking within the grade group
      const grade = rerankGrade;
      const allInGrade = readBooks.filter(b => b.grade === grade && b.id !== rerankingBook.id)
        .sort((a, b) => a.rank_in_group - b.rank_in_group);
      
      const bookOrder = [];
      let inserted = false;
      
      for (let i = 0; i < allInGrade.length; i++) {
        if (i === newRank - 1 && !inserted) {
          bookOrder.push(rerankingBook.book_id);
          inserted = true;
        }
        bookOrder.push(allInGrade[i].book_id);
      }
      
      if (!inserted) {
        bookOrder.push(rerankingBook.book_id);
      }
      
      await axios.put(`${API}/read-books/bulk-rank?grade=${grade}`, bookOrder, { withCredentials: true });
      
      toast.success(`Updated and re-ranked to #${newRank}!`);
      resetRerankDialog();
      fetchData();
    } catch (error) {
      console.error('Rerank error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const saveRerank = async (newRank) => {
    setSaving(true);
    try {
      const grade = rerankingBook.grade;
      const allInGrade = readBooks.filter(b => b.grade === grade).sort((a, b) => a.rank_in_group - b.rank_in_group);
      
      const withoutItem = allInGrade.filter(b => b.id !== rerankingBook.id);
      
      const bookOrder = [];
      let inserted = false;
      
      for (let i = 0; i < withoutItem.length; i++) {
        if (i === newRank - 1 && !inserted) {
          bookOrder.push(rerankingBook.book_id);
          inserted = true;
        }
        bookOrder.push(withoutItem[i].book_id);
      }
      
      if (!inserted) {
        bookOrder.push(rerankingBook.book_id);
      }
      
      await axios.put(`${API}/read-books/bulk-rank?grade=${grade}`, bookOrder, { withCredentials: true });
      
      toast.success(`Re-ranked to #${newRank}!`);
      resetRerankDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to update ranking');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrimaryGenre = async (bookId, genre) => {
    try {
      await axios.put(`${API}/read-books/${bookId}/primary-genre?primary_genre=${encodeURIComponent(genre)}`, {}, { withCredentials: true });
      
      setReadBooks(prev => prev.map(b => 
        b.id === bookId ? { ...b, primary_genre: genre } : b
      ));
      
      const statsRes = await axios.get(`${API}/read-books/stats`, { withCredentials: true });
      setStats(statsRes.data);
      
      toast.success('Genre updated');
    } catch (error) {
      toast.error('Failed to update genre');
    }
  };
  
  const openCustomGenreDialog = (bookId) => {
    setCustomGenreDialog({ open: true, bookId });
    setCustomGenreInput('');
  };
  
  const saveCustomGenre = async () => {
    if (!customGenreInput.trim() || !customGenreDialog.bookId) return;
    await handleUpdatePrimaryGenre(customGenreDialog.bookId, customGenreInput.trim());
    setCustomGenreDialog({ open: false, bookId: null });
    setCustomGenreInput('');
  };

  const resetRerankDialog = () => {
    setRerankingBook(null);
    setRerankStep('rating');
    setRerankGrade(null);
    setRerankComment('');
    setRerankDate(null);
    setComparisonStep(false);
    setExistingItems([]);
    setComparisonItem(null);
    setSearchLow(0);
    setSearchHigh(0);
  };

  const sortedBooks = [...readBooks]
    .filter(item => !selectedGenre || (item.primary_genre || item.genres?.[0]) === selectedGenre)
    .sort((a, b) => {
      const gradeOrder = { liked: 0, alright: 1, disliked: 2 };
      const gradeCompare = gradeOrder[a.grade] - gradeOrder[b.grade];
      if (gradeCompare !== 0) return gradeCompare;
      return a.rank_in_group - b.rank_in_group;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50">
      <AppHeader user={user} showBack={true} backPath="/books" />

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center shadow-lg">
              <Library className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3c405b]">Books Read</h1>
              <p className="text-[#64748B]">{stats?.total_books || 0} books read and ranked</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/books-discover')}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
            data-testid="add-read-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Book
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Read" value={stats?.total_books || 0} icon={<BookOpen className="w-5 h-5" />} color="coral" />
          <StatCard label="Liked" value={stats?.grade_counts?.liked || 0} icon={<ThumbsUp className="w-5 h-5" />} color="green" />
          <StatCard label="Alright" value={stats?.grade_counts?.alright || 0} icon={<Meh className="w-5 h-5" />} color="amber" />
          <StatCard label="Disliked" value={stats?.grade_counts?.disliked || 0} icon={<ThumbsDown className="w-5 h-5" />} color="slate" />
        </div>
        
        {/* Pages Read Stat */}
        {stats?.total_pages > 0 && (
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#3c405b]">{stats.total_pages.toLocaleString()}</p>
                <p className="text-sm text-[#64748B]">Total Pages Read</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Genre Breakdown */}
        {stats?.genre_stats && Object.keys(stats.genre_stats).length > 0 && (
          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-outfit text-lg text-[#3c405b]">By Genre</CardTitle>
                {selectedGenre && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedGenre(null)}
                    className="text-xs text-[#64748B] hover:text-[#3c405b]"
                  >
                    Clear filter
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Object.entries(stats.genre_stats).map(([genre, data]) => {
                  const config = getGenreConfig(genre);
                  const GenreIcon = config.icon || BookOpen;
                  const isSelected = selectedGenre === genre;
                  return (
                    <button 
                      key={genre} 
                      onClick={() => setSelectedGenre(isSelected ? null : genre)}
                      className={`flex items-center gap-3 p-3 rounded-xl border transition-all hover:scale-105 text-left ${
                        isSelected 
                          ? 'ring-2 ring-amber-500 ring-offset-2 ' + config.color
                          : config.color
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <GenreIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">{genre}</p>
                        <p className="text-xs opacity-75">{data.total} {data.total === 1 ? 'book' : 'books'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Score Unlock Progress */}
        {!showScores && (
          <Card className="mb-6 bg-white/95 backdrop-blur-sm border-0 shadow-xl" data-testid="score-unlock-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                  <Lock className="w-7 h-7 text-amber-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-outfit font-semibold text-[#3c405b] text-lg">Unlock Your Book Scores</h3>
                  <p className="text-[#64748B] text-sm">
                    Read {10 - (stats?.total_books || 0)} more books to reveal your personalized scores
                  </p>
                  <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-amber-500 rounded-full transition-all"
                      style={{ width: `${Math.min(((stats?.total_books || 0) / 10) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Books List */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3c405b]">
              {selectedGenre 
                ? `${selectedGenre} (${sortedBooks.length} books)`
                : `Your Rankings (${readBooks.length} books)`
              }
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sortedBooks.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium mb-2">No books read yet</p>
                <p className="text-sm mb-4">Start discovering and ranking books!</p>
                <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => navigate('/books-discover')}>
                  Discover Books
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedBooks.map((item, index) => {
                  const gradeConfig = {
                    liked: { icon: ThumbsUp, color: 'bg-green-100 text-green-700' },
                    alright: { icon: Meh, color: 'bg-amber-100 text-amber-700' },
                    disliked: { icon: ThumbsDown, color: 'bg-slate-100 text-slate-600' }
                  };
                  const grade = gradeConfig[item.grade] || gradeConfig.alright;
                  const GradeIcon = grade.icon;
                  const unifiedRank = index + 1;
                  
                  const primaryGenre = item.primary_genre || item.genres?.[0];
                  const genreInfo = primaryGenre ? getGenreConfig(primaryGenre) : null;
                  const hasMultipleGenres = item.genres?.length > 1;
                  
                  return (
                    <div
                      key={item.id}
                      className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
                      data-testid={`read-book-${item.id}`}
                    >
                      {/* Rank */}
                      <span className="font-outfit font-bold text-lg sm:text-xl text-[#3c405b] w-6 sm:w-8 text-center flex-shrink-0 pt-1">
                        {unifiedRank}
                      </span>
                      
                      {/* Book Cover */}
                      <div 
                        className="w-10 h-14 sm:w-12 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer hover:scale-105 transition-transform"
                        onClick={() => setViewingDetails(item)}
                      >
                        {item.cover_image ? (
                          <img src={item.cover_image} alt={item.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-slate-300" />
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setViewingDetails(item)}>
                          <h3 className="font-medium text-sm sm:text-base text-[#3c405b] truncate flex-1">{item.title}</h3>
                          {showScores && item.calculated_score !== null && item.calculated_score !== undefined && (
                            <div className="flex items-center gap-0.5 bg-amber-50 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid="score-display">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              <span className="font-medium text-xs text-amber-700">{Number(item.calculated_score).toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-1 text-xs text-[#94A3B8]">
                            {item.author && (
                              <span className="truncate max-w-[120px]">{item.author}</span>
                            )}
                            {item.read_date && (
                              <span className="hidden sm:flex items-center gap-1">
                                <span className="mx-1">•</span>
                                <Calendar className="w-3 h-3" />
                                {new Date(item.read_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-1">
                            {primaryGenre && (() => {
                              const GenreIcon = genreInfo?.icon || BookOpen;
                              return (
                                <div className={`p-1 rounded ${genreInfo?.color || 'bg-slate-100 text-slate-600'}`} title={primaryGenre}>
                                  <GenreIcon className="w-3 h-3" />
                                </div>
                              );
                            })()}
                            <div className={`p-1 rounded-full ${grade.color}`}>
                              <GradeIcon className="w-3 h-3" />
                            </div>
                          </div>
                        </div>
                        
                        {item.comment && (
                          <p className="hidden sm:block text-xs text-[#94A3B8] truncate mt-0.5 italic">&ldquo;{item.comment}&rdquo;</p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 text-[#64748B] hover:text-[#3c405b] flex-shrink-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {/* Change Genre - always show */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <div className="flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-100 rounded-sm">
                                <Tag className="w-4 h-4 mr-2 text-amber-500" />
                                Change Genre
                                <ChevronRight className="w-4 h-4 ml-auto" />
                              </div>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="left" align="start">
                              {(item.genres || []).map((genre) => {
                                const gConfig = getGenreConfig(genre);
                                const GIcon = gConfig?.icon || BookOpen;
                                const isSelected = genre === primaryGenre;
                                return (
                                  <DropdownMenuItem 
                                    key={genre}
                                    onClick={() => handleUpdatePrimaryGenre(item.id, genre)}
                                    className={`cursor-pointer ${isSelected ? 'bg-amber-50' : ''}`}
                                  >
                                    <GIcon className={`w-4 h-4 mr-2 ${gConfig?.color?.split(' ')[1] || 'text-slate-600'}`} />
                                    {genre}
                                    {isSelected && <Check className="w-4 h-4 ml-auto text-amber-500" />}
                                  </DropdownMenuItem>
                                );
                              })}
                              {/* Custom Genre Option */}
                              <DropdownMenuItem 
                                onClick={() => openCustomGenreDialog(item.id)}
                                className="cursor-pointer text-purple-600"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Custom Genre...
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <DropdownMenuItem onClick={() => handleStartRerank(item)}>
                            <RefreshCw className="w-4 h-4 mr-2" />Re-rank
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRemove(item)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Re-rank Dialog - Rating Step */}
      <Dialog open={!!rerankingBook && rerankStep === 'rating'} onOpenChange={() => resetRerankDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Update &amp; Re-rank
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Update your rating for &ldquo;{rerankingBook?.title}&rdquo;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Book Cover */}
            <div className="flex gap-4">
              <div className="w-20 h-28 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                {rerankingBook?.cover_image ? (
                  <img src={rerankingBook.cover_image} alt={rerankingBook.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-slate-300" />
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-medium text-[#3c405b]">{rerankingBook?.title}</h3>
                <p className="text-sm text-[#64748B]">{rerankingBook?.author}</p>
              </div>
            </div>
            
            {/* Grade Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#3c405b]">How did you feel about it?</label>
              <div className="flex gap-2">
                <Button
                  variant={rerankGrade === 'liked' ? 'default' : 'outline'}
                  onClick={() => setRerankGrade('liked')}
                  className={`flex-1 ${rerankGrade === 'liked' ? 'bg-green-500 hover:bg-green-600' : ''}`}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Liked
                </Button>
                <Button
                  variant={rerankGrade === 'alright' ? 'default' : 'outline'}
                  onClick={() => setRerankGrade('alright')}
                  className={`flex-1 ${rerankGrade === 'alright' ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                >
                  <Meh className="w-4 h-4 mr-2" />
                  Alright
                </Button>
                <Button
                  variant={rerankGrade === 'disliked' ? 'default' : 'outline'}
                  onClick={() => setRerankGrade('disliked')}
                  className={`flex-1 ${rerankGrade === 'disliked' ? 'bg-red-500 hover:bg-red-600' : ''}`}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  Disliked
                </Button>
              </div>
            </div>
            
            {/* Read Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#3c405b]">When did you read it?</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <Calendar className="mr-2 h-4 w-4" />
                    {rerankDate ? format(rerankDate, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={rerankDate}
                    onSelect={setRerankDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Comment */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#3c405b]">Your thoughts (optional)</label>
              <Textarea
                placeholder="What did you think about this book?"
                value={rerankComment}
                onChange={(e) => setRerankComment(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={resetRerankDialog}>Cancel</Button>
            <Button 
              onClick={handleContinueToComparison}
              disabled={!rerankGrade || saving}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Continue to Ranking
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-rank Dialog - Comparison Step */}
      <Dialog open={!!rerankingBook && rerankStep === 'comparison'} onOpenChange={() => resetRerankDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b] flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />Which do you prefer?
            </DialogTitle>
            <DialogDescription className="text-[#64748B]">
              Ranking &ldquo;{rerankingBook?.title}&rdquo; against your {rerankGrade} books
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-6">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleComparisonChoice(true)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {rerankingBook?.cover_image ? (
                    <img src={rerankingBook.cover_image} alt={rerankingBook.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-10 h-10 text-slate-300" /></div>
                  )}
                </div>
                <p className="font-medium text-[#3c405b] line-clamp-2 text-sm">{rerankingBook?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">Re-ranking</p>
              </button>
              
              <button
                onClick={() => handleComparisonChoice(false)}
                disabled={saving}
                className="p-4 rounded-xl border-2 border-slate-200 hover:border-amber-500 hover:bg-amber-50 transition-all text-left disabled:opacity-50"
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-slate-100 mb-3">
                  {comparisonItem?.cover_image ? (
                    <img src={comparisonItem.cover_image} alt={comparisonItem.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-10 h-10 text-slate-300" /></div>
                  )}
                </div>
                <p className="font-medium text-[#3c405b] line-clamp-2 text-sm">{comparisonItem?.title}</p>
                <p className="text-xs text-[#64748B] mt-1">#{comparisonItem?.rank_in_group}</p>
              </button>
            </div>
            
            {saving && (
              <div className="flex items-center justify-center mt-4">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Details Dialog */}
      <Dialog open={!!viewingDetails} onOpenChange={() => setViewingDetails(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-[#3c405b]">{viewingDetails?.title}</DialogTitle>
          </DialogHeader>
          {viewingDetails && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {viewingDetails.cover_image && (
                  <img 
                    src={viewingDetails.cover_image} 
                    alt={viewingDetails.title}
                    className="w-20 h-28 object-cover rounded-lg shadow"
                  />
                )}
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-[#64748B]">{viewingDetails.author}</p>
                  {viewingDetails.page_count > 0 && (
                    <p className="text-sm text-[#64748B]">{viewingDetails.page_count} pages</p>
                  )}
                  {viewingDetails.read_date && (
                    <p className="text-sm text-[#64748B] flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(viewingDetails.read_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                  {viewingDetails.is_first_read && (
                    <p className="text-sm text-purple-600 flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" />
                      First read
                    </p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#64748B]">Rating:</span>
                    {viewingDetails.grade === 'liked' && <span className="text-green-600 flex items-center gap-1"><ThumbsUp className="w-4 h-4"/> Liked</span>}
                    {viewingDetails.grade === 'alright' && <span className="text-amber-600 flex items-center gap-1"><Meh className="w-4 h-4"/> Alright</span>}
                    {viewingDetails.grade === 'disliked' && <span className="text-slate-600 flex items-center gap-1"><ThumbsDown className="w-4 h-4"/> Disliked</span>}
                  </div>
                </div>
              </div>
              {viewingDetails.comment && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-[#64748B] mb-1">Your thoughts:</p>
                  <p className="text-sm text-[#3c405b] italic">&ldquo;{viewingDetails.comment}&rdquo;</p>
                </div>
              )}
              {viewingDetails.genres?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {viewingDetails.genres.map((g) => (
                    <span key={g} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs">
                      {g}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Custom Genre Dialog */}
      <Dialog open={customGenreDialog.open} onOpenChange={(open) => !open && setCustomGenreDialog({ open: false, bookId: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#3D4F6F]">Enter Custom Genre</DialogTitle>
            <DialogDescription>
              Enter your own genre for this book
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="e.g., Literary Fiction, Cozy Mystery..."
            value={customGenreInput}
            onChange={(e) => setCustomGenreInput(e.target.value)}
            className="mt-2"
            data-testid="custom-genre-input"
            onKeyDown={(e) => e.key === 'Enter' && saveCustomGenre()}
          />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCustomGenreDialog({ open: false, bookId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={saveCustomGenre}
              disabled={!customGenreInput.trim()}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Save Genre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
